import React, { useEffect, useState } from 'react';
import { Table, Card, Space, Button, Input, message } from 'antd';
import axios from 'axios';
import 'antd/dist/reset.css';
import { API_FILE_PROCESS, API_TEST_PROCESS } from '../../utility';
import { getServiceUrl } from '../../config';

const RDX_API_BASE_URL = getServiceUrl();

const LabResultsTable = ({ data, onNextTab }) => {
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Flatten lab results from JSON output and add unique key.
  useEffect(() => {
    if (Array.isArray(data)) {
      const rows = data.flatMap((patient) =>
        (patient.json_output || []).map((result, index) => ({
          key: `${patient.id}-${index}`,
          ...result,
        })),
      );
      setResults(rows);
      setFilteredResults(rows);
    }
  }, [data]);

  // Handle search input change.
  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchText(searchValue);

    const filtered = results.filter((row) =>
      row.testName.toLowerCase().includes(searchValue),
    );
    setFilteredResults(filtered);
  };

  // Define table columns.
  const columns = [
    {
      title: 'Test Name',
      dataIndex: 'testName',
      key: 'testName',
      sorter: (a, b) => a.testName.localeCompare(b.testName),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => (
        <span
          style={{
            color:
              record.highOrLow === 'high'
                ? '#ff4d4f'
                : record.highOrLow === 'low'
                  ? '#1890ff'
                  : 'inherit',
          }}
        >
          {value} {record.unit || ''}
        </span>
      ),
    },
    {
      title: 'Reference Range',
      dataIndex: 'referenceRange',
      key: 'referenceRange',
    },
  ];

  // Configure row selection for the table.
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  // Handle the submission of selected rows.
  const handleSubmit = async () => {
    if (selectedRows.length === 0) {
      message.warning('Please select at least one lab result');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: data?.[0]?.patient_guid || 'patient_unknown',
        lab_results: selectedRows,
      };

      const { data: processedData } = await axios.post(
        `${API_TEST_PROCESS}/process_lab_results`,
        payload,
      );

      const hpoIdsArray = processedData.processed_results.map((item) => ({
        hpoid: item.HPOId,
        source: 'EHR',
        enddata: null,
        startdate: null,
        sourcefile: null,
      }));

      await axios.post(`${RDX_API_BASE_URL}doctors/savepatienthpoids`, {
        patientguid: processedData.id,
        hpoids: hpoIdsArray,
      });

      message.success('Lab results processed successfully');
      onNextTab?.();
    } catch (error) {
      console.error('Submission error:', error);
      message.error('Failed to process lab results');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title="Lab Results"
        className="my-6"
        extra={
          selectedRows.length > 0 && (
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              Process Selected Results
            </Button>
          )
        }
      >
        <Input.Search
          placeholder="Search by test name"
          onChange={handleSearchChange}
          style={{ marginBottom: 16 }}
        />
        <Table
          dataSource={filteredResults}
          columns={columns}
          rowSelection={rowSelection}
          pagination={{
            pageSize: 5,
          }}
        />
      </Card>
    </Space>
  );
};

export default LabResultsTable;
